import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Link, Navigate } from "react-router-dom";

import { ToastProvider } from "@/components/toast";
import { AuthProvider } from "@/contexts/AuthContext";

// Layouts
import CenteredBrandLayout from "@/layouts/CenteredBrandLayout";
import CompanyShellLayout from "@/layouts/CompanyShellLayout";
import BrandLockup from "@/components/BrandLockup";

// --- Lazy Pages ---
const LoginPage = lazy(() => import("@/pages/auth/LoginPage"));
const OwnerSignupPage = lazy(() => import("@/pages/owner/signup/OwnerSignupPage"));
const OwnerVerifyComplete = lazy(() => import("@/pages/auth/OwnerVerifyComplete"));
const EmployeeInviteComplete = lazy(() => import("@/pages/auth/EmployeeInviteComplete"));

const AuthCallback = lazy(() => import("@/pages/auth/AuthCallback"));
const ResetPasswordPage = lazy(() => import("@/pages/auth/ResetPasswordPage"));

const CreateCompanyPage = lazy(() => import("@/pages/company/CreateCompanyPage"));
const InviteEmployeePage = lazy(() => import("@/pages/company/InviteEmployeePage"));
const InviteEmployeeCenterPage = lazy(
  () => import("@/pages/company/InviteEmployeeCenterPage")
);
const OwnerCompanyHomePage = lazy(() => import("@/pages/company/OwnerCompanyHomePage"));
const ManageEmployeesPage = lazy(() => import("@/pages/company/ManageEmployeesPage"));
const PendingEmployeeFieldVerificationWorkspacePage = lazy(
  () =>
    import(
      "@/pages/company/PendingEmployeeFieldVerificationWorkspacePage"
    )
);
const EmployeeRecordPage = lazy(() => import("@/pages/company/EmployeeRecordPage"));
const CompanyRuleProposalWorkspacePage = lazy(
  () => import("@/pages/company/CompanyRuleProposalWorkspacePage")
);
const ImportTimeAwayBalancesPage = lazy(
  () => import("@/pages/company/ImportTimeAwayBalancesPage")
);
const CompanySettingsPage = lazy(
  () => import("@/pages/settings/CompanySettingsPage")
);
const EmployeeMetadataImportPage = lazy(
  () => import("@/pages/employees/EmployeeMetadataImportPage")
);

// --- Payroll Pages ---
const PayrollHomePage = lazy(() => import("@/pages/payroll/PayrollHomePage"));
const ClerkTimesheetsPage = lazy(() => import("@/pages/payroll/ClerkTimesheetsPage"));
const ClerkManualEntryPage = lazy(() => import("@/pages/payroll/ClerkManualEntryPage"));
const ClerkEmployeeTimesheetReviewPage = lazy(() => import("@/pages/payroll/ClerkEmployeeTimesheetReviewPage"));
const ClerkDisputesQueuePage = lazy(() => import("@/pages/payroll/ClerkDisputesQueuePage"));
const ClerkPreflightReportPage = lazy(() => import("@/pages/payroll/ClerkPreflightReportPage"));
const ClerkRunPayrollPage = lazy(() => import("@/pages/payroll/ClerkRunPayrollPage"));
const CreatePayrollPeriodPage = lazy(() => import("@/pages/payroll/CreatePayrollPeriodPage"));
const PayrollPeriodPage = lazy(() => import("@/pages/payroll/PayrollPeriodPage"));
const IssuerQueuePage = lazy(() => import("@/pages/payroll/IssuerQueuePage"));
const IssuerReviewRunPage = lazy(() => import("@/pages/payroll/IssuerReviewRunPage"));
const IssuerIssuePayrollPage = lazy(() => import("@/pages/payroll/IssuerIssuePayrollPage"));
const IssuedPayrollRunPage = lazy(() => import("@/pages/payroll/IssuedPayrollRunPage"));
const PayrollBlockerRepairPage = lazy(() => import("@/pages/payroll/PayrollBlockerRepairPage"));
const EmployeeHomePage = lazy(() => import("@/pages/payroll/EmployeeHomePage"));
const EmployeePayslipsPage = lazy(() => import("@/pages/payroll/EmployeePayslipsPage"));
const EmployeeTimesheetsPage = lazy(() => import("@/pages/payroll/EmployeeTimesheetsPage"));
const EmployeeDisputesPage = lazy(() => import("@/pages/payroll/EmployeeDisputesPage"));
const EmployeeAbsencesPage = lazy(() => import("@/pages/payroll/EmployeeAbsencesPage"));
const EmployeeLeavePage = lazy(() => import("@/pages/payroll/EmployeeLeavePage"));
const CompanyTimeAwayQueuePage = lazy(() => import("@/pages/payroll/CompanyTimeAwayQueuePage"));

// --- Simple Loader ---
const PageLoader = () => (
  <div className="min-h-screen flex flex-col items-center justify-center space-y-4 bg-gray-50">
    <div className="w-10 h-10 border-4 border-brand-vault border-t-transparent rounded-full animate-spin" />
    <p className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-400">
      Synchronizing Vault...
    </p>
  </div>
);

const NotFound = () => (
  <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
    <h1 className="text-4xl font-black text-gray-200">404</h1>
    <p className="font-black uppercase text-gray-300 tracking-[0.5em] mt-2">
      Vault Path Not Found
    </p>
  </div>
);

const PublicHomePage = () => (
  <div className="min-h-screen bg-gray-50 text-gray-900 overflow-x-hidden">
    <main className="relative flex min-h-screen items-center justify-center px-6 py-16">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute left-[-10%] top-[-8%] h-[24rem] w-[24rem] rounded-full bg-brand-accent/10 blur-3xl" />
        <div className="absolute bottom-[-10%] right-[-8%] h-[24rem] w-[24rem] rounded-full bg-brand-vault/10 blur-3xl" />
      </div>

      <div className="relative z-10 mx-auto flex w-full max-w-6xl flex-col items-center gap-12 text-center">
        <div className="space-y-6">
          <BrandLockup />
          <p className="mx-auto max-w-3xl text-lg font-medium leading-relaxed text-gray-600 md:text-2xl">
            Payroll done right, simply.
          </p>
          <p className="mx-auto max-w-3xl text-sm font-medium leading-relaxed text-gray-500 md:text-base">
            High-integrity payroll with company-scoped access, employee berthing,
            permission-shaped workspaces, and backend-owned control from first visit to issued payroll.
          </p>
        </div>

        <div className="grid w-full max-w-5xl grid-cols-1 gap-4 md:grid-cols-3">
          <Link
            to="/owner/signup"
            className="group rounded-[2rem] border border-gray-200 bg-white p-8 text-center shadow-sm transition-all hover:border-brand-accent hover:shadow-xl"
          >
            <p className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-400 group-hover:text-brand-accent">
              Start
            </p>
            <h2 className="mt-3 text-sm font-black uppercase tracking-[0.24em] text-gray-900">
              Create Company
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-gray-600">
              Start owner onboarding and establish your company workspace.
            </p>
          </Link>

          <Link
            to="/login"
            className="group rounded-[2rem] border border-gray-200 bg-white p-8 text-center shadow-sm transition-all hover:border-brand-accent hover:shadow-xl"
          >
            <p className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-400 group-hover:text-brand-accent">
              Access
            </p>
            <h2 className="mt-3 text-sm font-black uppercase tracking-[0.24em] text-gray-900">
              Sign In
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-gray-600">
              Owners and invited employees sign in here with verified credentials.
            </p>
          </Link>

          <a
            href="mailto:info@paycalcutility.com"
            className="group rounded-[2rem] border border-dashed border-gray-300 bg-white/70 p-8 text-center shadow-sm transition-all hover:border-brand-accent hover:bg-white hover:shadow-xl"
          >
            <p className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-400 group-hover:text-brand-accent">
              Support
            </p>
            <h2 className="mt-3 text-sm font-black uppercase tracking-[0.24em] text-gray-900">
              Talk to us
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-gray-600">
              Need help with setup, access, or workspace flow? Talk to us.
            </p>
          </a>
        </div>
      </div>
    </main>

    <section className="border-t border-gray-200 bg-white px-6 py-20">
      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-12 lg:grid-cols-2 lg:items-center">
        <div className="space-y-6">
          <div className="h-1 w-14 rounded-full bg-brand-accent" />
          <h2 className="text-4xl font-black tracking-tight text-gray-900 md:text-5xl">
            CLARITY through connected workflows.
          </h2>
          <p className="text-base leading-relaxed text-gray-600 md:text-lg">
            PayCalcUtility should feel like one product package. The public homepage,
            login, onboarding, company shell, and employee workspace all belong to the same
            system and should read that way from the first click onward.
          </p>
          <div className="flex flex-col gap-4 sm:flex-row">
            <Link
              to="/owner/signup"
              className="inline-flex items-center justify-center rounded-full bg-brand-accent px-8 py-4 text-[10px] font-black uppercase tracking-[0.3em] text-white shadow-lg transition-all hover:opacity-95"
            >
              Initialize Workspace
            </Link>
            <Link
              to="/login"
              className="inline-flex items-center justify-center rounded-full border border-gray-200 bg-white px-8 py-4 text-[10px] font-black uppercase tracking-[0.3em] text-gray-700 transition-all hover:bg-gray-50"
            >
              Go to Login
            </Link>
          </div>
        </div>

        <div className="space-y-6">
          <div className="rounded-[2rem] border border-gray-200 bg-gray-50 p-8 shadow-sm">
            <p className="text-[10px] font-black uppercase tracking-[0.3em] text-brand-accent">
              Employee Workspace
            </p>
            <p className="mt-4 text-sm leading-relaxed text-gray-600">
              All employees enter through the same authenticated landing surface. What they can
              do there is furnished by company authority, not by separate login screens.
            </p>
          </div>

          <div className="rounded-[2rem] bg-brand-vault p-8 text-white shadow-xl">
            <p className="text-[10px] font-black uppercase tracking-[0.3em] text-brand-accent">
              Company Integrity
            </p>
            <p className="mt-4 text-sm leading-relaxed text-gray-300">
              Owners can move across companies tied to their owner identity. Creator and delegate
              scope stays limited to companies created with their own login unless backend authority expands it.
            </p>
          </div>
        </div>
      </div>

      <div className="mx-auto mt-16 max-w-6xl border-t border-gray-200 pt-8 text-center">
        <a
          href="mailto:info@paycalcutility.com"
          className="text-[10px] font-black uppercase tracking-[0.35em] text-brand-accent transition-all hover:opacity-80"
        >
          Talk to us
        </a>
        <p className="mt-4 text-[9px] font-black uppercase tracking-[0.4em] text-gray-400">
          PayCalcUtility CLARITY © 2026
        </p>
      </div>
    </section>
  </div>
);

function AppRoutes() {
  return (
    <BrowserRouter>
      <Routes>
        <Route
          path="/"
          element={
            <Suspense fallback={<PageLoader />}>
              <PublicHomePage />
            </Suspense>
          }
        />

        <Route element={<CenteredBrandLayout />}>
          <Route
            path="/login"
            element={
              <Suspense fallback={<PageLoader />}>
                <LoginPage />
              </Suspense>
            }
          />
          <Route
            path="/auth/callback"
            element={
              <Suspense fallback={<PageLoader />}>
                <AuthCallback />
              </Suspense>
            }
          />
          <Route
            path="/reset-password"
            element={
              <Suspense fallback={<PageLoader />}>
                <ResetPasswordPage />
              </Suspense>
            }
          />

          <Route
            path="/owner/signup"
            element={
              <Suspense fallback={<PageLoader />}>
                <OwnerSignupPage />
              </Suspense>
            }
          />
          <Route
            path="/owner/verify-complete"
            element={
              <Suspense fallback={<PageLoader />}>
                <OwnerVerifyComplete />
              </Suspense>
            }
          />
          <Route
            path="/employee/verify-complete"
            element={
              <Suspense fallback={<PageLoader />}>
                <EmployeeInviteComplete />
              </Suspense>
            }
          />

          <Route
            path="/owner/create-company"
            element={
              <Suspense fallback={<PageLoader />}>
                <CreateCompanyPage />
              </Suspense>
            }
          />
        </Route>

        <Route path="/owner/company/:companyId" element={<CompanyShellLayout />}>
          <Route
            index
            element={
              <Suspense fallback={<PageLoader />}>
                <OwnerCompanyHomePage />
              </Suspense>
            }
          />

          <Route
            path="settings"
            element={
              <Suspense fallback={<PageLoader />}>
                <CompanySettingsPage />
              </Suspense>
            }
          />

          <Route
            path="invite-employee"
            element={
              <Suspense fallback={<PageLoader />}>
                <InviteEmployeeCenterPage />
              </Suspense>
            }
          />

          <Route
            path="employees/import-metadata"
            element={
              <Navigate
                to="../invite-employee"
                replace
              />
            }
          />

          <Route
            path="employees"
            element={
              <Suspense fallback={<PageLoader />}>
                <ManageEmployeesPage />
              </Suspense>
            }
          />
          <Route
            path="employees/pending-field-verification"
            element={
              <Suspense fallback={<PageLoader />}>
                <PendingEmployeeFieldVerificationWorkspacePage />
              </Suspense>
            }
          />
          <Route
            path="employees/import-balances"
            element={
              <Suspense fallback={<PageLoader />}>
                <ImportTimeAwayBalancesPage />
              </Suspense>
            }
          />
          <Route
            path="employees/:employeeId"
            element={
              <Suspense fallback={<PageLoader />}>
                <EmployeeRecordPage />
              </Suspense>
            }
          />

          <Route
            path="company-rules"
            element={
              <Suspense fallback={<PageLoader />}>
                <CompanyRuleProposalWorkspacePage />
              </Suspense>
            }
          />

          <Route path="payroll">
            <Route
              index
              element={
                <Suspense fallback={<PageLoader />}>
                  <PayrollHomePage />
                </Suspense>
              }
            />
            <Route
              path="timesheets"
              element={
                <Suspense fallback={<PageLoader />}>
                  <ClerkTimesheetsPage />
                </Suspense>
              }
            />
            <Route
              path="timesheets/manual-entry"
              element={
                <Suspense fallback={<PageLoader />}>
                  <ClerkManualEntryPage />
                </Suspense>
              }
            />
            <Route
              path="timesheets/review/:employeeId"
              element={
                <Suspense fallback={<PageLoader />}>
                  <ClerkEmployeeTimesheetReviewPage />
                </Suspense>
              }
            />
            <Route
              path="timesheets/disputes"
              element={
                <Suspense fallback={<PageLoader />}>
                  <ClerkDisputesQueuePage />
                </Suspense>
              }
            />
            <Route
              path="preflight"
              element={
                <Suspense fallback={<PageLoader />}>
                  <ClerkPreflightReportPage />
                </Suspense>
              }
            />
            <Route
              path="submit"
              element={
                <Suspense fallback={<PageLoader />}>
                  <ClerkRunPayrollPage />
                </Suspense>
              }
            />
            <Route
              path="time-away"
              element={
                <Suspense fallback={<PageLoader />}>
                  <CompanyTimeAwayQueuePage />
                </Suspense>
              }
            />

            <Route path="periods">
              <Route
                path="new"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <CreatePayrollPeriodPage />
                  </Suspense>
                }
              />
              <Route
                path=":periodId"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <PayrollPeriodPage />
                  </Suspense>
                }
              />
            </Route>

            <Route
              path="issuer"
              element={
                <Suspense fallback={<PageLoader />}>
                  <IssuerQueuePage />
                </Suspense>
              }
            />
            <Route
              path="run/:runId/review"
              element={
                <Suspense fallback={<PageLoader />}>
                  <IssuerReviewRunPage />
                </Suspense>
              }
            />
            <Route
              path="run/:runId/repair/:assignmentId"
              element={
                <Suspense fallback={<PageLoader />}>
                  <PayrollBlockerRepairPage />
                </Suspense>
              }
            />
            <Route
              path="run/:runId/issue"
              element={
                <Suspense fallback={<PageLoader />}>
                  <IssuerIssuePayrollPage />
                </Suspense>
              }
            />
            <Route
              path="issued/:runId"
              element={
                <Suspense fallback={<PageLoader />}>
                  <IssuedPayrollRunPage />
                </Suspense>
              }
            />

            <Route
              path="my"
              element={
                <Suspense fallback={<PageLoader />}>
                  <EmployeeHomePage />
                </Suspense>
              }
            />
            <Route
              path="my/payslips"
              element={
                <Suspense fallback={<PageLoader />}>
                  <EmployeePayslipsPage />
                </Suspense>
              }
            />
            <Route
              path="my/timesheets"
              element={
                <Suspense fallback={<PageLoader />}>
                  <EmployeeTimesheetsPage />
                </Suspense>
              }
            />
            <Route
              path="my/disputes"
              element={
                <Suspense fallback={<PageLoader />}>
                  <EmployeeDisputesPage />
                </Suspense>
              }
            />
            <Route
              path="my/absences"
              element={
                <Suspense fallback={<PageLoader />}>
                  <EmployeeAbsencesPage />
                </Suspense>
              }
            />
            <Route
              path="my/leave"
              element={
                <Suspense fallback={<PageLoader />}>
                  <EmployeeLeavePage />
                </Suspense>
              }
            />

            <Route path="*" element={<NotFound />} />
          </Route>

          <Route path="*" element={<NotFound />} />
        </Route>

        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <ToastProvider>
        <AppRoutes />
      </ToastProvider>
    </AuthProvider>
  );
}
